import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import BarChart from './components/BarChart';
import DetailsButton from './components/DetailsButton';
import PeriodBlockTooltip from './components/PeriodBlockTooltip';
import usePeriodBlockHandlers from './hooks/usePeriodBlockHandlers';

const sanitizeId = str => str.replace(/[^a-zA-Z0-9]/g, '-');

const useStyles = makeStyles(theme => ({
  periodColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    position: 'relative',
    padding: theme.spacing(1, 2, 0),
    gap: theme.spacing(0.25),
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    transition: 'background-color 0.2s ease, border-bottom 0.3s ease',
    cursor: 'pointer',
    borderBottom: `${theme.spacing(0.25)}px solid transparent`,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1, 0)
    },
    '&:focus-visible': {
      outline: `${theme.spacing(0.25)}px solid ${theme.palette.primary.main}`,
      outlineOffset: theme.spacing(0.25),
      zIndex: 10
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        '& $detailsButton': {
          color: theme.palette.primary.main
        }
      }
    }
  },
  periodColumnSelected: {
    backgroundColor: theme.palette.action.hover,
    borderBottom: `${theme.spacing(0.25)}px solid ${
      theme.palette.primary.main
    }`,
    '& $detailsButton': {
      opacity: 0
    }
  },
  periodLabel: {
    marginBottom: theme.spacing(1),
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  detailsButton: {}
}));

const PeriodBlock = memo(
  ({ data, isSelected = false, onClick, maxValue, unitLabel }) => {
    const classes = useStyles();
    const { formatMessage } = useIntl();
    const isMobile = useIsBreakpointDown('sm');
    const { period, startDate, bench, overAllocation } = data;

    const { handleClick, handleKeyDown } = usePeriodBlockHandlers({
      startDate,
      onClick
    });

    const containerClassName = useMemo(
      () =>
        classNames(classes.periodColumn, {
          [classes.periodColumnSelected]: isSelected
        }),
      [classes.periodColumn, classes.periodColumnSelected, isSelected]
    );

    const tooltipContentId = useMemo(
      () => `bench-overallocation-tooltip-${sanitizeId(period)}`,
      [period]
    );

    const tooltipContent = useMemo(
      () => (
        <div id={tooltipContentId}>
          <PeriodBlockTooltip
            period={period}
            bench={bench}
            overAllocation={overAllocation}
            unitLabel={unitLabel}
          />
        </div>
      ),
      [period, bench, overAllocation, unitLabel, tooltipContentId]
    );

    const periodAriaLabel = useMemo(
      () =>
        formatMessage(
          {
            id:
              'resourceManagementDashboard.benchOverAllocationTracker.timelineDetailSection'
          },
          { period }
        ),
      [formatMessage, period]
    );

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        aria-describedby={tooltipContentId}
      >
        <Box
          className={containerClassName}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-label={periodAriaLabel}
          aria-controls="bench-detail-panel"
          aria-expanded={isSelected ? 'true' : 'false'}
          data-qe-id={`periodBlock${period}`}
        >
          <Typography
            aria-hidden
            className={classes.periodLabel}
            variant={isMobile ? 'body2' : 'body1'}
            data-qe-id="periodLabel"
          >
            {period}
          </Typography>

          <BarChart
            bench={bench}
            overAllocation={overAllocation}
            maxValue={maxValue}
            unitLabel={unitLabel}
          />

          <DetailsButton className={classes.detailsButton} />
        </Box>
      </Tooltip>
    );
  }
);

PeriodBlock.propTypes = {
  data: PropTypes.shape({
    period: PropTypes.string.isRequired,
    startDate: PropTypes.object,
    bench: PropTypes.number,
    overAllocation: PropTypes.number
  }).isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  maxValue: PropTypes.number.isRequired,
  unitLabel: PropTypes.string
};

export default PeriodBlock;
